<template>
  <div class="flex-container">
    <div v-show="!isShowBulkDealManagement"  class="main-area">
      <div class="title">
        Ürün Komisyon Teklifleri
      </div>
      <div class="filter-area-top">
        <h3>Komisyon Dönemleri</h3>
        <div
          ref="scrollableRef"
          class="periods"
          @pointerdown="pointerDownFn"
          @pointerup="pointerUpFn"
        >
          <div
            v-for="(period, index) in periodList"
            :key="index"
            :class="['date', { selected: isPeriodSelected(period, index) }]"
            @click="changePeriodPayload(period)"
          >
            {{ period['dealName'] }}
          </div>
        </div>
      </div>
      <div class="filter-area-bottom">
        <ProductDealFilters
          ref="filterComponentRef"
          @filter-emitter="changeFilterPayload"
        />
      </div>
      <div class="list-area box">
        <TableForDeal
          :data="data"
          :columns="columns"
          :is-loading="isLoading"
          :page-count="pageCount"
          :pagination="getPaginationForTable()"
          @onPageChanged="changePaginationPayload"
          @go-bulk-deal-management="isShowBulkDealManagement = true"
        />
      </div>
    </div>
    <BulkDealManagement
      v-show="isShowBulkDealManagement"
      @go-deal-main-page="isShowBulkDealManagement = false"
      :start-date="payload.startDate"
      :finish-date="payload.finishDate"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import ProductDealFilters from './partials/ProductDealFilters';
import TableForDeal from './partials/TableForDeal';
import { columns, getTableDataAsync } from './services/productDealService';
import BulkDealManagement from './BulkDealManagement/BulkDealManagement.vue';

const sessionKey = 'PRODUCT_DEAL_PAYLOAD';
const defaultPayload = {
  startDate: null,
  finishDate: null,
  productName: '',
  productCode: '',
  stockCode: '',
  gtin: '',
  status: null,
  page: 0,
  pageSize: 10,
};

const scrollableRef = ref(null);
const filterComponentRef = ref(null);
const periodList = ref([]);
const isLoading = ref(true);
const data = ref([]);
const pageCount = ref(0);
const payload = ref({ ...defaultPayload });
const isShowBulkDealManagement = ref(false);

let pointerFrom = 0;
let elementFrom = 0;

// Reset responsive features until the responsive design is ready.
const resetResponsive = () => {
  document.getElementById('app').style.minWidth = '1320px';
  document.querySelector('#app .topMenu').style.cssText =
    'display:flex !important';
  document.querySelector('#app .navbar').style.cssText =
    'display:block !important;margin-top:unset;';
};

const onDrag = (event) => {
  if (event.pointerType === 'mouse') {
    scrollableRef.value.scrollLeft = elementFrom - event.clientX + pointerFrom;
  }
};

const pointerDownFn = (event) => {
  const { target, pointerType } = event;

  if (target.classList.contains('date') || pointerType !== 'mouse') {
    return;
  }

  pointerFrom = event.clientX;
  elementFrom = scrollableRef.value.scrollLeft;
  document.addEventListener('pointermove', onDrag);
};

const pointerUpFn = (event) => {
  if (event.pointerType === 'mouse') {
    document.removeEventListener('pointermove', onDrag);
  }
};

const getPayloadFromSession = () => {
  const sessionObj = JSON.parse(sessionStorage.getItem(sessionKey));
  if (!sessionObj) return false;

  const today = new Date().toISOString().slice(0, 10);

  if (sessionObj.savedDate !== today) {
    sessionStorage.removeItem(sessionKey);
    return false;
  }

  payload.value = {
    ...payload.value,
    ...sessionObj,
  };

  return true;
};


const savePayloadToSession = (page, pageSize, startDate, finishDate) => {
  const today = new Date().toISOString().slice(0, 10);
  const sessionObj = JSON.stringify({
    page,
    pageSize,
    startDate,
    finishDate,
    savedDate: today,
  });
  sessionStorage.setItem(sessionKey, sessionObj);
};

const sendRequestAsync = async () => {
  isLoading.value = true;
  try {
    const response = await getTableDataAsync(payload.value);
    if (response.error) return;
    const { page, pageSize, startDate, finishDate } = payload.value;

    if (!startDate && !finishDate) {
      const initialPeriod = response.periodList[0];
      payload.value = {
        ...payload.value,
        startDate: initialPeriod.startDate,
        finishDate: initialPeriod.finishDate,
      };
      savePayloadToSession(page, pageSize, initialPeriod.startDate, initialPeriod.finishDate);
    } else {
      savePayloadToSession(page, pageSize, startDate, finishDate);
    }

    data.value = response.data;
    pageCount.value = response.pageCount;
    periodList.value.length === 0 && (periodList.value = response.periodList);

  } catch (error) {
      throw error
  } finally {
      isLoading.value = false;
  }
};

const clearPayload = () => {
  payload.value = { ...defaultPayload };
};

const changeFilterPayload = (newFiltersPayload) => {
  payload.value = {
    ...payload.value,
    ...newFiltersPayload,
    page: 0,
  };
};

const changePeriodPayload = (period) => {
  const { startDate, finishDate } = payload.value;

  if (startDate === period.startDate && finishDate === period.finishDate) {
    return;
  }

  filterComponentRef.value.clearFilters();
  clearPayload();
  payload.value = {
    ...payload.value,
    ...period,
  };
};

const changePaginationPayload = (pagination) => {
  const { page, pageSize } = payload.value;
  if (page === pagination.page && pageSize === pagination.pageSize) return;
  payload.value = {
    ...payload.value,
    ...pagination,
  };
};

const getPaginationForTable = () => {
  return {
    page: payload.value.page,
    pageSize: payload.value.pageSize,
  };
};

const isPeriodSelected = (period, index) => {
  const { startDate, finishDate } = payload.value;

  if (startDate && finishDate) {
    return period.startDate === startDate && period.finishDate === finishDate;
  }

  return index === 0;
};

onMounted(async () => {
  resetResponsive();
  const sessionValid = getPayloadFromSession();

  if (!sessionValid) {
    payload.value = { ...defaultPayload };
  }
});

watch(payload, () => sendRequestAsync());
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/utilities';

.flex-container {
  display: flex;
  width: 100%;

  .main-area {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
  }

  .title {
    padding: 16px 0;
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.2px;
    line-height: 26px;
  }

  .filter-area-top {
    display: flex;
    flex-direction: column;
    padding: 24px 24px 16px;
    margin-bottom: 2px;
    background: $colorN00;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    gap: 16px;

    h3 {
      color: $colorN80;
      font-size: 16px;
      font-weight: 700;
      letter-spacing: -0.05px;
      line-height: 20px;
    }

    .periods {
      display: flex;
      overflow: scroll;
      gap: 12px;
      -ms-overflow-style: none;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      .date {
        display: flex;
        width: 300px;
        height: 42px;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        border: 0.5px solid $colorD30;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: -0.05px;
        line-height: 18px;
        user-select: none;

        &.selected {
          border: 2px solid $colorM50;
          background: $colorM05;
          color: $colorN80;
          font-weight: 700;
        }
      }
    }
  }

  .filter-area-bottom {
    width: 100%;
    padding: 24px;
    margin-bottom: 16px;
    background: $colorN00;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }

  .list-area {
    border-radius: 8px;

    &.box {
      width: 100%;
      padding: 24px;
      background: $colorN00;
    }
  }
}
</style>
